import type { GameEvent } from "../../engine/types";

export const INSECT_EVENTS: readonly GameEvent[] = [
  {
    id: "i_young_feast",
    archetypes: ["INSECT"],
    stages: ["larva", "nymph"],
    weight: 10,
    text: "รอบตัวคุณเต็มไปด้วยอาหาร",
    choices: [
      {
        label: "กินไม่หยุด",
        outcomes: [
          { chance: 0.8, text: "คุณกินจนตัวโตขึ้นอย่างเห็นได้ชัด", effects: { fullness: 35 } },
          { chance: 0.2, text: "นกตัวหนึ่งเหลือบมาเห็นคุณ", effects: { fullness: 30, safety: -20 } },
        ],
      },
      { label: "กินแล้วซ่อนตัวใต้ใบไม้", outcomes: [{ chance: 1, text: "ไม่มีใครเห็นคุณ", effects: { fullness: 20, safety: 10 } }] },
    ],
  },
  {
    id: "i_molt",
    archetypes: ["INSECT"],
    stages: ["larva", "nymph"],
    weight: 4,
    text: "ผิวของคุณเริ่มคับแน่น ถึงเวลาลอกคราบ",
    choices: [
      {
        label: "ลอกคราบเดี๋ยวนี้",
        outcomes: [
          { chance: 0.9, text: "คุณออกจากคราบเก่า ตัวใหญ่ขึ้นกว่าเดิม", effects: { health: 10, fullness: -10 } },
          { chance: 0.1, text: "คราบติดขาไว้ข้างหนึ่ง ต้องใช้เวลานานกว่าจะหลุด", effects: { health: -20, cause: "accident" } },
        ],
      },
      { label: "หาที่ปลอดภัยก่อน", outcomes: [{ chance: 1, text: "คุณลอกคราบอย่างปลอดภัยในซอกใบไม้", effects: { health: 5, fullness: -15, safety: 5 } }] },
    ],
  },
  {
    id: "i_parasitoid",
    archetypes: ["INSECT"],
    stages: ["larva"],
    weight: 2,
    text: "แตนเบียนตัวเล็กๆ บินวนอยู่รอบตัวคุณ",
    choices: [
      {
        label: "ดิ้นแรงๆ ไล่มัน",
        outcomes: [
          { chance: 0.6, text: "มันยอมแพ้และบินจากไป" },
          { chance: 0.4, text: "มันวางไข่ไว้ในตัวคุณได้สำเร็จ", effects: { health: -45, cause: "disease" } },
        ],
      },
      {
        label: "อยู่นิ่งๆ",
        outcomes: [
          { chance: 0.5, text: "มันมองไม่เห็นคุณ" },
          { chance: 0.5, text: "มันวางไข่ไว้ในตัวคุณได้สำเร็จ", effects: { health: -45, cause: "disease" } },
        ],
      },
    ],
  },
  {
    id: "i_pupa_bird",
    archetypes: ["INSECT"],
    stages: ["pupa"],
    weight: 4,
    text: "นกตัวหนึ่งเกาะอยู่บนกิ่งไม้ใกล้ดักแด้ของคุณ",
    choices: [
      {
        label: "อยู่นิ่งที่สุด",
        outcomes: [
          { chance: 0.75, text: "มันมองเห็นแค่ใบไม้แห้ง แล้วก็บินจากไป", effects: { safety: 5 } },
          { chance: 0.25, text: "มันจิกดักแด้ของคุณ", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "i_flowers",
    archetypes: ["INSECT"],
    stages: ["adult"],
    requires: { subgroups: ["insect.butterfly", "insect.bee_wasp", "insect.fly"] },
    weight: 8,
    text: "ดอกไม้บานสะพรั่งเต็มทุ่ง",
    choices: [
      {
        label: "บินจากดอกหนึ่งไปอีกดอก",
        outcomes: [
          { chance: 0.85, text: "น้ำหวานเต็มไปหมด และคุณช่วยผสมเกสรไปด้วย", effects: { fullness: 35 } },
          { chance: 0.15, text: "แมงมุมปูซ่อนอยู่ในดอกไม้ คุณหนีออกมาได้อย่างหวุดหวิด", effects: { fullness: 20, safety: -20 } },
        ],
      },
      { label: "อยู่ที่ดอกเดียวนานๆ", outcomes: [{ chance: 1, text: "ได้น้ำหวานพอสมควร", effects: { fullness: 20 } }] },
    ],
  },
  {
    id: "i_adult_food",
    archetypes: ["INSECT"],
    stages: ["adult"],
    requires: { subgroups: ["insect.beetle", "insect.bug", "insect.grasshopper", "insect.dragonfly", "insect.other"] },
    weight: 8,
    text: "คุณเจออาหารที่ชอบ",
    choices: [
      {
        label: "กินให้อิ่ม",
        outcomes: [
          { chance: 0.8, text: "อิ่มแล้ว พร้อมสำหรับวันข้างหน้า", effects: { fullness: 35 } },
          { chance: 0.2, text: "จิ้งจกตัวหนึ่งจ้องคุณอยู่", effects: { fullness: 25, safety: -15 } },
        ],
      },
      { label: "กินนิดหน่อยแล้วบินไป", outcomes: [{ chance: 1, text: "ปลอดภัยไว้ก่อน", effects: { fullness: 15, safety: 5 } }] },
    ],
  },
  {
    id: "i_light_trap",
    archetypes: ["INSECT"],
    stages: ["adult"],
    requires: { canFly: true, habitat: ["urban", "forest", "grassland"] },
    weight: 3,
    threatenedWeight: 2,
    text: "แสงไฟสว่างจ้าในยามค่ำคืน ดึงดูดคุณอย่างแรง",
    choices: [
      {
        label: "บินเข้าหาแสง",
        outcomes: [
          { chance: 0.5, text: "คุณบินวนรอบแสงไฟจนเหนื่อย", effects: { fullness: -15 } },
          { chance: 0.4, text: "คุณบินชนหลอดไฟจนปีกเสียหาย", effects: { health: -25, cause: "human" } },
          { chance: 0.1, text: "ตุ๊กแกรออยู่ใต้ไฟอย่างอดทน", effects: { death: "predator" } },
        ],
      },
      { label: "ฝืนบินหนีไป", outcomes: [{ chance: 1, text: "คุณบินกลับเข้าความมืดได้สำเร็จ", effects: { fullness: -10 } }] },
    ],
  },
  {
    id: "i_web",
    archetypes: ["INSECT"],
    stages: ["adult"],
    requires: { canFly: true },
    weight: 2,
    text: "ข้างหน้ามีใยแมงมุมขึงอยู่ระหว่างกิ่งไม้",
    choices: [
      { label: "บินอ้อม", outcomes: [{ chance: 1, text: "ไกลกว่าเดิมนิดหน่อย แต่ปลอดภัย", effects: { fullness: -5 } }] },
      {
        label: "บินลอดช่องเล็กๆ",
        outcomes: [
          { chance: 0.6, text: "ลอดผ่านไปได้อย่างหวุดหวิด" },
          { chance: 0.4, text: "ปีกของคุณติดใย แมงมุมวิ่งมาแล้ว", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "i_mate_swarm",
    archetypes: ["INSECT"],
    stages: ["adult"],
    requires: { notFlags: ["has_mate"] },
    weight: 6,
    text: "แมลงชนิดเดียวกับคุณบินมารวมกันเป็นกลุ่ม",
    choices: [
      {
        label: "บินเข้าไปหาคู่",
        outcomes: [
          { chance: 0.7, text: "คุณเจอคู่แล้ว!", effects: { addFlags: ["has_mate"], fullness: -10 } },
          { chance: 0.3, text: "นกนางแอ่นโฉบผ่านกลุ่มแมลงไปหลายรอบ คุณหนีออกมาก่อน", effects: { safety: -15 } },
        ],
      },
      { label: "รอดูอยู่ห่างๆ", outcomes: [{ chance: 1, text: "กลุ่มแมลงสลายไปก่อนที่คุณจะตัดสินใจ" }] },
    ],
  },
];
